import {
  useModalOldContext,
  WSAlert,
  WSButton,
  WSButtons,
  WSModalOld,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { ICreditCard } from "@wingspanhq/users";
import React from "react";
import { useHistory } from "react-router-dom";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryPayeeRows } from "../../../query/search/payee/queries/useQueryPayeeRows";
import { useMutationUpdateClient } from "../../../query/users/mutations";
import { useClientQuery } from "../../../query/users/queries";
import {
  PAYMENT_METHOD_REPLACEMENT_ALERT_BODY,
  PAYMENT_METHOD_REPLACEMENT_ALERT_TITLE
} from "../../../shared/constants/paymentMethodReplacement";
import { removeElementFromArray } from "../../../utils/serviceHelper";
import { PATH_PAYMENT_ADD_PAYMENT_METHOD } from "../../paths";
import { CreditCardPreview } from "../CreditCardPreview/CreditCardPreview";

export const REMOVE_CREDIT_CARD_MODAL_KEY = "removeCreditCard";

type RemoveCreditCardModalContentProps = {
  paymentMethod: ICreditCard;
  index: number;
  onClose: () => void;
};

// Extracted so its hooks run only while the modal is open, and so it's testable without the legacy modal harness.
export const RemoveCreditCardModalContent: React.FC<
  RemoveCreditCardModalContentProps
> = ({ paymentMethod, index, onClose }) => {
  const userId = useUserId();
  const history = useHistory();
  const clientQuery = useClientQuery(userId);
  const [updateClient, updateClientMeta] = useMutationUpdateClient(userId);

  const isDefaultMethod =
    !!paymentMethod?.paymentMethodId &&
    clientQuery.data?.profile.defaultPaymentMethod?.paymentMethodId ===
      paymentMethod.paymentMethodId;
  // Mirrors backend enforcement: a payer with any Active engagement must keep a method on file.
  // Only fetched when removing the default card (others can't orphan it).
  const activeEngagementsQuery = useQueryPayeeRows(
    {
      filter: { "engagements.status": PayerPayeeEngagementStatus.Active },
      page: { size: 1 }
    },
    { enabled: isDefaultMethod }
  );

  const clientLoaded = !!clientQuery.data;
  // Stable disabled action until client + policy state are known (no action swap, a11y).
  const isDeterminingRemoval =
    !clientLoaded ||
    (isDefaultMethod && activeEngagementsQuery.isInitialLoading);
  const mustReplaceBeforeRemoval =
    isDefaultMethod && (activeEngagementsQuery.data?.length ?? 0) > 0;

  const goToAddPaymentMethod = () => {
    onClose();
    // Return to the current page (e.g. the invoice being paid) after adding a method.
    const returnTo = `${history.location.pathname}${history.location.search}`;
    const search = new URLSearchParams({ returnTo }).toString();
    history.push(`${PATH_PAYMENT_ADD_PAYMENT_METHOD}?${search}`);
  };

  const removeCard = () => {
    updateClient(
      {
        clientId: userId,
        profile: {
          savedPaymentMethods: removeElementFromArray(
            clientQuery.data?.profile.savedPaymentMethods?.length || 0,
            index
          ),
          // Clear the default in the same update so a rejection can't orphan it.
          ...(isDefaultMethod
            ? {
                defaultPaymentMethod: { accountId: null, paymentMethodId: null }
              }
            : {})
        }
      },
      {
        onSuccess: onClose
      }
    );
  };

  return (
    <>
      <WSText mb="XL">Are you sure you want to remove this card?</WSText>
      <CreditCardPreview paymentMethod={paymentMethod} mb="XL" />

      {mustReplaceBeforeRemoval && (
        <WSAlert
          mb="XL"
          icon="info-circle"
          theme="info"
          title={PAYMENT_METHOD_REPLACEMENT_ALERT_TITLE}
        >
          {PAYMENT_METHOD_REPLACEMENT_ALERT_BODY}
        </WSAlert>
      )}

      <WSErrorMessage
        mb="XL"
        contextKey="AddPaymentMethod"
        error={updateClientMeta.error}
      />

      <WSButtons format="modal">
        {mustReplaceBeforeRemoval ? (
          <WSButton onClick={goToAddPaymentMethod}>Add payment method</WSButton>
        ) : (
          <WSButton
            destructive
            onClick={removeCard}
            loading={updateClientMeta.isLoading || isDeterminingRemoval}
          >
            Remove
          </WSButton>
        )}
        <WSButton.Tertiary onClick={onClose}>Cancel</WSButton.Tertiary>
      </WSButtons>
    </>
  );
};

export const RemoveCreditCardModal: React.FC = () => {
  const { closeModal } = useModalOldContext();

  return (
    <WSModalOld
      name={REMOVE_CREDIT_CARD_MODAL_KEY}
      size="S"
      title="Remove card"
    >
      {({ paymentMethod, index }) => (
        <RemoveCreditCardModalContent
          paymentMethod={paymentMethod}
          index={index}
          onClose={() => closeModal(REMOVE_CREDIT_CARD_MODAL_KEY)}
        />
      )}
    </WSModalOld>
  );
};
